import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../hooks/useApi';
import { getCreditPacks, createCreditSetupIntent, type CreditPack } from '../services/credits';
import CreditEmbeddedCheckout from '../components/CreditEmbeddedCheckout';
import { useTracking } from '../hooks/useTracking';

export default function CreditsPage() {
  const { user, isAuthenticated } = useAuth();
  const { user: userData, loading: userLoading } = useUser();
  const navigate = useNavigate();
  const { track } = useTracking();
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(null);
  const [checkoutCustomerId, setCheckoutCustomerId] = useState<string | null>(null);
  const [selectedPackId, setSelectedPackId] = useState<'basic' | 'pro' | 'premium' | null>(null);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);

  // CRITICAL: Clear stale OAuth flags to prevent random redirects from /credits
  // This is a revenue-protection measure - users on checkout must not be redirected
  useEffect(() => {
    if (isAuthenticated) {
      try {
        sessionStorage.removeItem('oauth_initiated_time');
        sessionStorage.removeItem('oauth_provider');
        console.log('[CreditsPage] Cleared stale OAuth flags to prevent redirect');
      } catch (e) {
        console.error('[CreditsPage] Failed to clear OAuth flags:', e);
      }
    }
  }, [isAuthenticated]);

  const fetchCreditPacks = useCallback(async () => {
    if (!userData?.subscription_tier) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getCreditPacks();
      
      // Filter packs to only show the one matching user's subscription tier
      const filteredPacks = data.packs.filter(
        pack => pack.id.toLowerCase() === userData.subscription_tier?.toLowerCase()
      );
      
      if (filteredPacks.length === 0) {
        setError(`No credit pack available for your ${userData.subscription_tier} subscription tier.`);
      }
      
      setPacks(filteredPacks);
    } catch (err) {
      console.error('Failed to fetch credit packs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load credit packs');
    } finally {
      setLoading(false);
    }
  }, [userData]);

  // Track view_content when page loads - global deduplication in useTracking will prevent duplicates
  useEffect(() => {
    if (isAuthenticated && !userLoading) {
      track('view_content', {
        content_type: 'credits_page',
        page_path: '/credits',
      });
    }
  }, [isAuthenticated, userLoading, track]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Wait for user data to load
    if (userLoading) {
      return;
    }
    
    // Check if user has active subscription
    if (userData) {
      if (!userData.subscription_tier) {
        setError('You need an active subscription to purchase credits. Please subscribe to a plan first.');
        setLoading(false);
        return;
      }
      fetchCreditPacks();
    }
  }, [isAuthenticated, navigate, userData, userLoading, fetchCreditPacks]);

  const purchasePack = async (packId: 'basic' | 'pro' | 'premium') => {
    if (!user || !user.email) {
      setError('User email is required');
      return;
    }

    if (!userData?.subscription_tier) {
      setError('You need an active subscription to purchase credits.');
      return;
    }

    // Verify pack matches subscription tier
    if (packId !== userData.subscription_tier.toLowerCase()) {
      setError(`You can only purchase ${userData.subscription_tier} credit packs with your current subscription.`);
      return;
    }

    // Track add_to_cart event
    const pack = packs.find((p) => p.id === packId);
    track('add_to_cart', {
      plan_id: packId,
      plan_name: pack?.name || packId,
      plan_price: pack?.price_usd || 0,
      currency: 'USD',
      value: pack?.price_usd || 0,
    });

    try {
      setPurchasing(packId);
      setError(null);
      
      const response = await createCreditSetupIntent({
        pack_id: packId,
        email: user.email!,
        user_id: user.id,
      });

      if (response.success && response.client_secret && response.customer_id) {
        setCheckoutClientSecret(response.client_secret);
        setCheckoutCustomerId(response.customer_id);
        setSelectedPackId(packId);
        
        // Store publishable key from backend response (if provided)
        if (response.publishable_key) {
          setStripePublishableKey(response.publishable_key);
        }
        
        setShowCheckout(true);
        
        // Track begin_checkout event
        track('begin_checkout', {
          plan_id: packId,
          plan_name: pack?.name || packId,
          plan_price: pack?.price_usd || 0,
          currency: 'USD',
          value: pack?.price_usd || 0,
        });
      } else {
        throw new Error('Failed to initialize checkout');
      }
    } catch (err) {
      console.error('Failed to create checkout:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setPurchasing(null);
    }
  };

  const handleCheckoutComplete = () => {
    setShowCheckout(false);
    setCheckoutClientSecret(null);
    setCheckoutCustomerId(null);
    setSelectedPackId(null);
    setStripePublishableKey(null);
    setPurchasing(null);
    
    // Refresh credit balance
    if (typeof window !== 'undefined' && (window as any).refreshCreditBalance) {
      (window as any).refreshCreditBalance();
    }
    
    // Show success message
    setError(null);
    // You could add a success state here if needed
    navigate('/', { replace: true });
  };

  const handleCheckoutError = (error: Error) => {
    setError(error.message);
    setShowCheckout(false);
    setPurchasing(null);
  };

  const handleCloseCheckout = () => {
    setShowCheckout(false);
    setCheckoutClientSecret(null);
    setCheckoutCustomerId(null);
    setSelectedPackId(null);
    setPurchasing(null);
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  if (userLoading || (loading && !error)) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading credit packs...</p>
        </div>
      </div>
    );
  }

  // Show error if no subscription
  if (userData && !userData.subscription_tier) {
    return (
      <div className="min-h-screen bg-bg text-white p-4 sm:p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">Buy Credits</h1>
            <p className="text-white/70 text-sm sm:text-base">
              Purchase credit packs to generate videos and images
            </p>
          </div>

          <div className="p-6 bg-red-500/20 border border-red-500/50 rounded-xl">
            <h2 className="text-xl font-bold text-red-400 mb-2">Subscription Required</h2>
            <p className="text-white/80 mb-4">
              You need an active subscription to purchase credits. Please subscribe to a plan first.
            </p>
            <button
              onClick={() => navigate('/billing')}
              className="bg-white text-black px-6 py-3 rounded-full font-semibold hover:bg-gray-200 transition-colors"
            >
              View Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-white p-4 sm:p-6 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Buy Credits</h1>
          <p className="text-white/70 text-sm sm:text-base">
            Purchase credit packs to generate videos and images
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packs.map((pack) => {
            const packId = pack.name.toLowerCase().replace(' pack', '') as 'basic' | 'pro' | 'premium';
            const isPurchasing = purchasing === packId;
            
            return (
              <div
                key={pack.id}
                className="bg-white/5 border border-white/20 rounded-xl p-6 flex flex-col"
              >
                <h3 className="text-xl font-bold mb-2">{pack.name}</h3>
                <div className="mb-4">
                  <p className="text-3xl font-bold text-white mb-1">
                    {pack.credits} <span className="text-lg text-white/70">Credits</span>
                  </p>
                  <p className="text-2xl font-semibold text-white">${pack.price_usd}</p>
                </div>
                <p className="text-white/70 text-sm mb-6 flex-grow">{pack.description}</p>
                <button
                  onClick={() => purchasePack(packId)}
                  disabled={isPurchasing || purchasing !== null}
                  className="w-full bg-white text-black px-6 py-3 rounded-full font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isPurchasing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    'Purchase'
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-8 p-6 bg-white/5 border border-white/20 rounded-xl">
          <h2 className="text-xl font-bold mb-4">Credit Costs</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-white/70">Video Generation:</span>
              <span className="text-white font-semibold">100 credits</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/70">Image Generation:</span>
              <span className="text-white font-semibold">1 credit</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/70">Image Edit:</span>
              <span className="text-white font-semibold">2 credits</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/70">Upload/Enhancement:</span>
              <span className="text-white font-semibold">Free</span>
            </div>
          </div>
        </div>
      </div>

      {/* Embedded Checkout Modal */}
      {showCheckout && checkoutClientSecret && checkoutCustomerId && selectedPackId && user && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-bg border border-white/20 rounded-xl p-6 w-full max-w-2xl my-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Purchase Credits</h2>
              <button
                onClick={handleCloseCheckout}
                className="text-white/60 hover:text-white transition-colors"
                aria-label="Close"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <CreditEmbeddedCheckout
              clientSecret={checkoutClientSecret}
              customerId={checkoutCustomerId}
              packId={selectedPackId}
              userId={user.id}
              packPrice={packs.find((p) => p.id === selectedPackId)?.price_usd}
              publishableKey={stripePublishableKey || undefined}
              onComplete={handleCheckoutComplete}
              onError={handleCheckoutError}
            />
          </div>
        </div>
      )}
    </div>
  );
}

